import { useState, useEffect, useRef, useCallback } from 'react'
import Icon from '@/components/ui/Icon'
import styles from './ActivityTimer.module.css'

interface ActivityTimerProps {
  durationMinutes: number
  onComplete?: () => void
}

export default function ActivityTimer({ durationMinutes, onComplete }: ActivityTimerProps) {
  const totalSeconds = durationMinutes * 60
  const [remaining, setRemaining] = useState(totalSeconds)
  const [running, setRunning] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval>>()
  const completedRef = useRef(false)

  const handleComplete = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    try { navigator.vibrate?.(200) } catch { /* ignore */ }
    onComplete?.()
  }, [onComplete])

  useEffect(() => {
    if (!running || remaining <= 0) return
    intervalRef.current = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current)
          handleComplete()
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(intervalRef.current)
  }, [running, remaining, handleComplete])

  const minutes = Math.floor(remaining / 60)
  const seconds = remaining % 60
  const isComplete = remaining <= 0
  const progress = totalSeconds > 0 ? remaining / totalSeconds : 0

  // SVG circle math
  const radius = 44
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference * (1 - progress)

  return (
    <div className={styles.container}>
      <div className={styles.ring}>
        <svg width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={radius} className={styles.trackCircle} />
          <circle
            cx="50"
            cy="50"
            r={radius}
            className={`${styles.progressCircle} ${isComplete ? styles.completeCircle : ''}`}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <div className={`${styles.timeDisplay} ${isComplete ? styles.timeComplete : ''}`}>
          {isComplete
            ? "Time's up!"
            : `${minutes}:${String(seconds).padStart(2, '0')}`}
        </div>
      </div>
      {!isComplete && (
        <div className={styles.controls}>
          <button
            className={styles.controlButton}
            onClick={() => setRunning(!running)}
            title={running ? 'Pause' : 'Resume'}
          >
            {running ? <Icon name="pause" size={20} /> : <Icon name="play" size={20} />}
          </button>
        </div>
      )}
    </div>
  )
}
